/**
 * Portal ICAVI - Cliente HTTP centralizado (Axios)
 * Exporta a instância configurada do axios com interceptors de autenticação e
 * renovação de token. Este módulo NÃO contém lógica de domínio de negócio.
 *
 * Outros módulos de serviço devem importar `api` daqui em vez de criar
 * instâncias próprias do axios.
 */
import axios from 'axios';
import { setupProgressInterceptors } from './progressInterceptor';
import { refreshTokenStorage } from './refreshTokenStorage';
import { clearSharedAccessToken } from './tokenRefreshFlow';
import { isSessionInvalid401 } from './authFailurePolicy';
import { clearWebSessionMarker, getWebSessionMarker, WEB_CSRF_COOKIE_NAMES, WEB_SESSION_COOKIE_NAMES, WEB_SESSION_MARKER, } from './authSession';
import { connectivityGuard, isNetworkError } from './connectivityGuard';
import { isSuccessfulWebSessionRefresh, refreshWebSession } from './webSessionRefresh';
// ============================================================================
// URL Base
// ============================================================================
/**
 * Detecta automaticamente a URL base da API baseada no hostname atual.
 */
const getApiBaseUrl = () => {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    const port = window.location.port;
    // Nginx (porta 80, 443, ou sem porta): URL relativa para proxy
    if (!port || port === '' || port === '80' || port === '443' || port === '3001') {
        return '';
    }
    // webpack-dev-server na :8080 → backend em :5000
    if ((hostname === 'localhost' || hostname === '127.0.0.1') && port === '8080') {
        return 'http://localhost:5000';
    }
    // IP de rede local: forçar HTTP explicitamente
    if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
        const isLocalIP = /^(192\.168\.|10\.|172\.(1[6-9]|2[0-9]|3[01])\.)/.test(hostname);
        const forcedProtocol = isLocalIP ? 'http' : protocol;
        return `${forcedProtocol}//${hostname}:5000`;
    }
    return '';
};
export const API_BASE_URL = getApiBaseUrl();
// ============================================================================
// Auth force-logout
// ============================================================================
const AUTH_FORCE_LOGOUT_EVENT = 'icavi:auth-force-logout';
export const DEVE_TROCAR_SENHA_EVENT = 'icavi:deve-trocar-senha';
const dispatchAuthForceLogout = (reason = 'session_invalidated') => {
    if (typeof window === 'undefined') {
        return;
    }
    if (window.location.pathname.startsWith('/public/')) {
        return;
    }
    try {
        const event = typeof CustomEvent === 'function'
            ? new CustomEvent(AUTH_FORCE_LOGOUT_EVENT, { detail: { reason } })
            : new Event(AUTH_FORCE_LOGOUT_EVENT);
        window.dispatchEvent(event);
    }
    catch (error) {
        console.error('Erro ao despachar evento de logout forcado:', error);
    }
};
const readCookie = (name) => {
    if (typeof document === 'undefined') {
        return null;
    }
    const prefix = `${name}=`;
    const rawCookie = document.cookie
        .split(';')
        .map(cookie => cookie.trim())
        .find(cookie => cookie.startsWith(prefix));
    if (!rawCookie) {
        return null;
    }
    try {
        return decodeURIComponent(rawCookie.slice(prefix.length));
    }
    catch {
        return rawCookie.slice(prefix.length);
    }
};
const isUnsafeMethod = (method) => !['get', 'head', 'options'].includes((method || 'get').toLowerCase());
const isJsonMutationMethod = (method) => {
    const normalized = (method || 'get').toLowerCase();
    return normalized === 'post' || normalized === 'put' || normalized === 'patch';
};
const isMultipartBody = (data) => {
    if (typeof FormData !== 'undefined' && data instanceof FormData) {
        return true;
    }
    if (typeof URLSearchParams !== 'undefined' && data instanceof URLSearchParams) {
        return true;
    }
    return false;
};
/**
 * SecurityMiddleware rejeita POST/PUT/PATCH sem mimetype JSON.
 * Axios 1.18 remove Content-Type quando `data` é undefined (XHR).
 */
export const ensureJsonMutation = (config) => {
    if (!isJsonMutationMethod(config.method) || isMultipartBody(config.data)) {
        return config;
    }
    if (config.data === undefined) {
        config.data = {};
    }
    config.headers.set('Content-Type', 'application/json');
    return config;
};
const readFirstCookie = (names) => {
    for (const name of names) {
        const value = readCookie(name);
        if (value) {
            return value;
        }
    }
    return null;
};
const isWebSessionPresent = () => Boolean(readFirstCookie(WEB_SESSION_COOKIE_NAMES) || getWebSessionMarker());
const addWebCsrfHeader = (config) => {
    if (!isUnsafeMethod(config.method)) {
        return;
    }
    const csrfToken = readFirstCookie(WEB_CSRF_COOKIE_NAMES);
    if (csrfToken) {
        config.headers['X-ICAVI-CSRF'] = csrfToken;
    }
};
const resolveFetchHref = (input) => {
    if (typeof input === 'string') {
        return input;
    }
    if (input instanceof URL) {
        return input.href;
    }
    if (typeof Request !== 'undefined' && input instanceof Request) {
        return input.url;
    }
    return String(input);
};
/**
 * Cookie/CSRF rewrite applies only to Portal (same-origin or API_BASE_URL).
 * Third-party fetches (Clicksign embedded widget) must keep credentials omit:
 * Clicksign CORS uses Access-Control-Allow-Origin: *, incompatible with include.
 */
export const isPortalFetchTarget = (rawUrl) => {
    if (typeof window === 'undefined') {
        return true;
    }
    try {
        const url = new URL(rawUrl, window.location.href);
        if (url.origin === window.location.origin) {
            return true;
        }
        if (!API_BASE_URL) {
            return false;
        }
        return url.origin === new URL(API_BASE_URL, window.location.href).origin;
    }
    catch {
        return true;
    }
};
export const installWebFetchBridge = () => {
    if (typeof window === 'undefined' || window.fetch.name === 'icaviWebFetchBridge') {
        return;
    }
    const nativeFetch = window.fetch.bind(window);
    const bridgedFetch = (async (input, init) => {
        if (!isPortalFetchTarget(resolveFetchHref(input))) {
            return nativeFetch(input, init);
        }
        const headers = new Headers(init?.headers || (input instanceof Request ? input.headers : undefined));
        const hasCompatibilityMarker = headers.get('Authorization')?.trim() === `Bearer ${WEB_SESSION_MARKER}`;
        const hasWebSession = isWebSessionPresent() || hasCompatibilityMarker;
        if (!hasWebSession) {
            return nativeFetch(input, init);
        }
        const method = (init?.method || (input instanceof Request ? input.method : 'GET')).toLowerCase();
        headers.delete('Authorization');
        if (isUnsafeMethod(method)) {
            const csrfToken = readFirstCookie(WEB_CSRF_COOKIE_NAMES);
            if (csrfToken) {
                headers.set('X-ICAVI-CSRF', csrfToken);
            }
        }
        return nativeFetch(input, {
            ...init,
            credentials: init?.credentials || 'include',
            headers,
        });
    });
    Object.defineProperty(bridgedFetch, 'name', { value: 'icaviWebFetchBridge' });
    window.fetch = bridgedFetch;
};
const isAuthEndpoint = (url = '') => url.includes('/api/auth/web') ||
    url.includes('/api/auth/me') ||
    url.includes('/api/login') ||
    url.includes('/api/refresh-token');
// ============================================================================
// Instância Axios
// ============================================================================
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json, text/plain, */*',
    },
    withCredentials: true,
    timeout: 120000, // 120 segundos (operações complexas com Tangerino)
});
// ============================================================================
// Interceptors
// ============================================================================
/**
 * Interceptor de requisição: envia cookies Web e a prova CSRF legível.
 */
api.interceptors.request.use((config) => {
    delete config.headers.Authorization;
    ensureJsonMutation(config);
    addWebCsrfHeader(config);
    return config;
}, (error) => Promise.reject(error));
/**
 * Interceptor de resposta: trata erros 401 e renova tokens automaticamente.
 */
api.interceptors.response.use((response) => {
    // Comunicação OK: encerra qualquer janela de indisponibilidade/sessão inválida.
    connectivityGuard.recordSuccess();
    return response;
}, async (error) => {
    const originalRequest = error.config;
    // Erro de rede/proxy (sem resposta HTTP): abre o circuito e loga uma vez.
    if (isNetworkError(error)) {
        connectivityGuard.recordNetworkError();
        if (connectivityGuard.shouldLogOutageOnce()) {
            console.warn('[ICAVI] Backend temporariamente inacessível; pollers em backoff.');
        }
        return Promise.reject(error);
    }
    // Backend bloqueou por senha não trocada — forçar modal a reaparecer
    if (error.response?.status === 403 &&
        error.response?.data?.codigo === 'DEVE_TROCAR_SENHA') {
        window.dispatchEvent(new CustomEvent(DEVE_TROCAR_SENHA_EVENT));
        return Promise.reject(error);
    }
    if (error.response?.status === 401 &&
        !originalRequest._retry &&
        !isAuthEndpoint(originalRequest?.url) &&
        isWebSessionPresent()) {
        originalRequest._retry = true;
        if (isSuccessfulWebSessionRefresh(await refreshWebSession())) {
            return api(originalRequest);
        }
        const shouldForceLogout = isSessionInvalid401(error.response?.status, error.response?.data);
        if (!shouldForceLogout) {
            return Promise.reject(error);
        }
        clearWebSessionMarker();
        clearSharedAccessToken();
        localStorage.removeItem('userData');
        localStorage.removeItem('token');
        refreshTokenStorage.clear();
        connectivityGuard.markSessionInvalidated();
        dispatchAuthForceLogout('refresh_failed_or_session_invalidated');
        console.warn('Token invalido ou expirado. Usuario precisa fazer login novamente.');
    }
    if (error.response?.status === 401 && originalRequest._retry) {
        clearWebSessionMarker();
        clearSharedAccessToken();
        localStorage.removeItem('userData');
        localStorage.removeItem('token');
        refreshTokenStorage.clear();
        connectivityGuard.markSessionInvalidated();
        dispatchAuthForceLogout('session_invalidated_after_retry');
    }
    return Promise.reject(error);
});
// Configura interceptors de progresso (NProgress ou similar)
setupProgressInterceptors(api);
// ============================================================================
// Exports
// ============================================================================
/**
 * Instância interna do axios — usada pelos serviços de domínio.
 * Não expor diretamente para componentes React; use `apiService` ou um
 * serviço de domínio específico.
 */
export { api };
/**
 * Alias público nomeado — mantém compatibilidade com importadores que usam
 * `import { apiInstance } from 'services/core/apiService'`.
 */
export const apiInstance = api;
